"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UNCATEGORISED = exports.CertificationStateSchema = exports.ExamAttemptSchema = exports.AcademyTrackSchema = exports.AcademyQuestionSchema = exports.AcademyChoiceSchema = exports.AcademyModuleSchema = exports.AcademyChapterSchema = exports.ChapterVideoSchema = exports.CERTIFICATION_STATES = exports.LEARNER_TYPES = void 0;
exports.gradeAttempt = gradeAttempt;
exports.didPass = didPass;
exports.drawQuestions = drawQuestions;
exports.toLearnerQuestion = toLearnerQuestion;
exports.isExamUnlocked = isExamUnlocked;
exports.cooldownRemainingMs = cooldownRemainingMs;
exports.categoryOf = categoryOf;
exports.groupModulesByCategory = groupModulesByCategory;
exports.isActivatingTrack = isActivatingTrack;
exports.applyExclusiveActivation = applyExclusiveActivation;
const zod_1 = require("zod");
/**
 * Academy — an audience-neutral course + exam library.
 *
 * Certification of affiliate partners is consumer #1. The `audience` field on a
 * module is the seam that lets the same videos become customer help content and
 * employee training later without a migration.
 */
exports.LEARNER_TYPES = ['affiliate', 'customer', 'employee'];
exports.CERTIFICATION_STATES = [
    'not_started',
    'in_progress',
    'failed',
    'passed',
    'bypassed',
];
exports.ChapterVideoSchema = zod_1.z.object({
    key: zod_1.z.string(),
    cdnUrl: zod_1.z.string(),
    durationSeconds: zod_1.z.number().nonnegative().optional(),
    sizeBytes: zod_1.z.number().nonnegative().optional(),
    contentType: zod_1.z.string(),
    uploadedAt: zod_1.z.number(),
});
exports.AcademyChapterSchema = zod_1.z.object({
    chapterId: zod_1.z.string(),
    moduleId: zod_1.z.string(),
    title: zod_1.z.string().min(1),
    summary: zod_1.z.string().default(''),
    order: zod_1.z.number().int().nonnegative(),
    required: zod_1.z.boolean().default(true),
    video: exports.ChapterVideoSchema.optional(),
});
exports.AcademyModuleSchema = zod_1.z.object({
    moduleId: zod_1.z.string(),
    title: zod_1.z.string().min(1),
    summary: zod_1.z.string().default(''),
    order: zod_1.z.number().int().nonnegative(),
    audience: zod_1.z.array(zod_1.z.enum(exports.LEARNER_TYPES)).default(['affiliate']),
    status: zod_1.z.enum(['draft', 'published']).default('draft'),
    updatedAt: zod_1.z.number(),
    updatedBy: zod_1.z.string().default(''),
    /** Groups the module for browsing — "Sales", "Marketing", "Development". */
    category: zod_1.z.string().optional(),
    /** Tenants see this module only if one of these is in `tenant.enabledPackages`. [] = everyone in the audience. */
    packageIds: zod_1.z.array(zod_1.z.string()).default([]),
});
exports.AcademyChoiceSchema = zod_1.z.object({
    id: zod_1.z.string(),
    text: zod_1.z.string().min(1),
});
exports.AcademyQuestionSchema = zod_1.z.object({
    questionId: zod_1.z.string(),
    chapterId: zod_1.z.string(),
    prompt: zod_1.z.string().min(1),
    choices: zod_1.z.array(exports.AcademyChoiceSchema).min(2),
    /** Never serialised to a learner. Use toLearnerQuestion(). */
    correctChoiceId: zod_1.z.string(),
    explanation: zod_1.z.string().optional(),
});
exports.AcademyTrackSchema = zod_1.z.object({
    trackId: zod_1.z.string(),
    title: zod_1.z.string().min(1),
    moduleIds: zod_1.z.array(zod_1.z.string()).default([]),
    passMarkPercent: zod_1.z.number().min(1).max(100).default(80),
    questionCount: zod_1.z.number().int().min(1).default(15),
    shuffleQuestions: zod_1.z.boolean().default(true),
    shuffleChoices: zod_1.z.boolean().default(true),
    cooldownMinutesAfterFail: zod_1.z.number().int().nonnegative().default(60),
    audience: zod_1.z.enum(exports.LEARNER_TYPES).default('affiliate'),
    version: zod_1.z.number().int().nonnegative().default(1),
    published: zod_1.z.boolean().default(false),
    /**
     * The ONE certification that switches a partner's referral link on. At most
     * one track may carry it; `putTrack` enforces that server-side. Every other
     * certification is a specialisation that awards a badge and nothing more.
     */
    activatesPartner: zod_1.z.boolean().optional(),
    /** Hidden from learners and from new assignment. Never hard-deleted: a
     *  rep's pass is the evidence of why they were activated. */
    archived: zod_1.z.boolean().optional(),
});
exports.ExamAttemptSchema = zod_1.z.object({
    attemptId: zod_1.z.string(),
    trackId: zod_1.z.string(),
    startedAt: zod_1.z.number(),
    submittedAt: zod_1.z.number().optional(),
    questionIds: zod_1.z.array(zod_1.z.string()),
    /** Snapshot taken at attempt start. NEVER serialised to a learner. */
    answerKey: zod_1.z.record(zod_1.z.string()),
    answers: zod_1.z.record(zod_1.z.string()).optional(),
    correctCount: zod_1.z.number().int().nonnegative().optional(),
    scorePercent: zod_1.z.number().optional(),
    passed: zod_1.z.boolean().optional(),
    trackVersion: zod_1.z.number().int().nonnegative(),
});
exports.CertificationStateSchema = zod_1.z.object({
    trackId: zod_1.z.string(),
    status: zod_1.z.enum(exports.CERTIFICATION_STATES).default('not_started'),
    bestScorePercent: zod_1.z.number().optional(),
    attemptCount: zod_1.z.number().int().nonnegative().default(0),
    cooldownUntil: zod_1.z.number().optional(),
    passedAt: zod_1.z.number().optional(),
    /**
     * True once academy-service has had a 200 back from affiliate-service's
     * /internal/certification-passed for this pass. Additive and optional: an
     * absent value on an older record means "unknown", which is treated as
     * NOT confirmed so the activation call is retried rather than skipped.
     * Without this, a pass whose activation call failed leaves the learner
     * holding a certificate for an affiliate that was never switched on, with
     * no path back — replay and retake both short-circuit on `passed`.
     */
    activationConfirmed: zod_1.z.boolean().optional(),
    bypassedBy: zod_1.z.string().optional(),
    bypassedAt: zod_1.z.number().optional(),
    bypassReason: zod_1.z.enum(['admin', 'legacy']).optional(),
});
// ---------------------------------------------------------------------------
// Pure exam logic. No AWS, no I/O — this is the whole gate, so it is all tested.
// ---------------------------------------------------------------------------
/**
 * Grade an attempt against the answer key snapshotted when it started.
 * A missing answer is wrong. Answers for questions outside the key are ignored,
 * so a client cannot inflate its own total.
 */
function gradeAttempt(answerKey, answers) {
    const ids = Object.keys(answerKey);
    const total = ids.length;
    if (total === 0)
        return { correctCount: 0, total: 0, scorePercent: 0 };
    const correctCount = ids.filter((id) => answers[id] === answerKey[id]).length;
    // Stored score is rounded for display only — see didPass for the real gate.
    const scorePercent = Math.round((correctCount / total) * 1000) / 10;
    return { correctCount, total, scorePercent };
}
/**
 * The pass decision. Deliberately computed from the exact ratio and NOT from
 * the rounded scorePercent, so rounding can never turn a fail into a pass.
 */
function didPass(correctCount, total, passMarkPercent) {
    if (total <= 0)
        return false;
    return (correctCount / total) * 100 >= passMarkPercent;
}
/**
 * Fisher-Yates draw. `rand` is injectable so tests are deterministic.
 * Never mutates the pool; never returns a duplicate.
 */
function drawQuestions(pool, count, rand = Math.random) {
    const copy = [...pool];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rand() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy.slice(0, Math.min(count, copy.length));
}
/** Strip everything a learner must not see. The only way questions leave the API. */
function toLearnerQuestion(q) {
    return {
        questionId: q.questionId,
        chapterId: q.chapterId,
        prompt: q.prompt,
        choices: q.choices.map((c) => ({ id: c.id, text: c.text })),
    };
}
function isExamUnlocked(requiredChapterIds, completedChapterIds) {
    const done = new Set(completedChapterIds);
    return requiredChapterIds.every((id) => done.has(id));
}
function cooldownRemainingMs(cooldownUntil, now) {
    if (!cooldownUntil)
        return 0;
    return Math.max(0, cooldownUntil - now);
}
// ---------------------------------------------------------------------------
// Certification grouping and exclusivity logic.
// ---------------------------------------------------------------------------
exports.UNCATEGORISED = 'Uncategorised';
/** A module's group. Blank, whitespace and absent all collapse to one bucket. */
function categoryOf(module) {
    const c = (module.category || '').trim();
    return c || exports.UNCATEGORISED;
}
/**
 * Groups modules for the curriculum list. Categories sort alphabetically with
 * Uncategorised pinned last, so newly-added modules never push the named
 * groups around.
 */
function groupModulesByCategory(items) {
    const byCategory = new Map();
    for (const item of items) {
        const key = categoryOf(item.module);
        const bucket = byCategory.get(key);
        if (bucket)
            bucket.push(item);
        else
            byCategory.set(key, [item]);
    }
    return [...byCategory.entries()]
        .map(([category, group]) => ({
        category,
        items: [...group].sort((a, b) => a.module.order - b.module.order),
    }))
        .sort((a, b) => {
        if (a.category === exports.UNCATEGORISED)
            return 1;
        if (b.category === exports.UNCATEGORISED)
            return -1;
        return a.category.localeCompare(b.category);
    });
}
function isActivatingTrack(t) {
    return t.activatesPartner === true;
}
/**
 * THE EXCLUSIVITY RULE. Exactly one certification may activate a partner, so
 * setting the flag anywhere clears it everywhere else. Pure and total: the
 * caller persists whatever this returns. Never mutates its input.
 */
function applyExclusiveActivation(tracks, activatingTrackId) {
    return tracks.map((t) => ({ ...t, activatesPartner: t.trackId === activatingTrackId }));
}
//# sourceMappingURL=academy.js.map